import React, { useCallback, useEffect, useState } from 'react';
import { Address as AddressType } from 'shared/types/account';
import Link from 'components/commercetools-ui/atoms/link';
import Message from 'components/commercetools-ui/atoms/message';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount } from 'frontastic';
import Address from './address';
import { AddressFormData } from './address-form';
import usePropsToAddressType from './mapPropsToAddressType';

const Addresses = () => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const { mapPropsToAddress } = usePropsToAddressType();

  const { account, defaultBillingAddress, defaultShippingAddress } = useAccount();
  const addresses = account?.addresses;

  const [selectedBillingAddress, setSelectedBillingAddress] = useState<AddressType>(
    defaultBillingAddress as AddressType,
  );
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<AddressType>(
    defaultShippingAddress as AddressType,
  );

  const selectAddress = (address: AddressFormData) => {
    address.isBillingAddress ? setSelectedBillingAddress(address) : setSelectedShippingAddress(address);
  };

  const setAddressAsDefault = useCallback(
    (address: AddressType) => {
      const { setAsDefault } = mapPropsToAddress(address as AddressFormData);
      setAsDefault();
    },
    [account],
  );

  useEffect(() => {
    if (selectedBillingAddress?.addressId !== defaultBillingAddress?.addressId) {
      setAddressAsDefault(selectedBillingAddress as AddressType);
    }
  }, [selectedBillingAddress]);

  useEffect(() => {
    if (selectedShippingAddress?.addressId !== defaultShippingAddress?.addressId) {
      setAddressAsDefault(selectedShippingAddress as AddressType);
    }
  }, [selectedShippingAddress]);

  const [successMsg, setSuccessMsg] = useState('');
  let checkSuccessMsg = true;

  useEffect(() => {
    const addressAction = localStorage.getItem('addressAction');

    if (addressAction && checkSuccessMsg) {
      checkSuccessMsg = false;
      let successMessage = '';
      switch (addressAction) {
        case 'add':
          successMessage = formatAccountMessage({
            id: 'address.add.success.message',
            defaultMessage: 'Address has been added successfully.',
          });
          break;
        case 'update':
          successMessage = formatAccountMessage({
            id: 'address.update.success.message',
            defaultMessage: 'Address has been successfully updated.',
          });
          break;
        case 'delete':
          successMessage = formatAccountMessage({
            id: 'address.delete.success.message',
            defaultMessage: 'Address has been deleted.',
          });
          break;
        default:
          successMessage = '';
      }
      setSuccessMsg(successMessage);
      localStorage.removeItem('addressAction');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 500);
    }
  }, []);

  return (
    <div className="mt-20 px-16 md:px-24 lg:mt-40 lg:px-44">
      <div className="hidden pb-12 md:block md:pb-20 2xl:pb-36">
        <Typography as="h2" className="text-22 text-primary-black lg:text-24">
          {formatAccountMessage({
            id: 'addresses',
            defaultMessage: 'Addresses',
          })}
        </Typography>
      </div>
      <div>
        <Typography className="mb-28 text-14 leading-loose text-secondary-black md:mb-24 md:text-16 2xl:mb-36">
          {formatAccountMessage({
            id: 'address.desc',
            defaultMessage: 'Manage or add addresses for your account.',
          })}
        </Typography>
        <Link
          link="?hash=addresses&id=address-add"
          className="block w-full rounded-md bg-gray-700 px-16 py-8 text-center text-14 font-medium leading-[114%] text-neutral-150 hover:cursor-pointer md:w-fit 2xl:px-40 2xl:py-12"
        >
          {formatAccountMessage({ id: 'address.add', defaultMessage: 'Add an address' })}
        </Link>
      </div>
      {successMsg && (
        <div className="mt-32 w-[100%]">
          <Message severity="success" message={successMsg} />
        </div>
      )}
      <form className="mt-24 grid gap-20 pb-28 md:mt-28 2xl:mt-32" noValidate>
        {addresses?.map((address) => (
          <Address
            key={address.addressId}
            address={address}
            selectAddress={selectAddress}
            isDefaultAddress={address?.isDefaultBillingAddress || address?.isDefaultShippingAddress || false}
          />
        ))}
      </form>
    </div>
  );
};

export default Addresses;
